/**
 * LMSR (Logarithmic Market Scoring Rule) calculations
 *
 * Client-side implementation for accurate price impact calculations.
 * Uses log-sum-exp trick for numerical stability.
 *
 * All calculations use human-readable scale (not contract scale).
 */

// =============================================================================
// Core LMSR Math
// =============================================================================

/**
 * Compute LMSR cost function: C(q) = b * ln(sum(exp(q_i / b)))
 * Uses log-sum-exp trick for numerical stability.
 *
 * @param quantities - Current quantities per bucket
 * @param liquidityParam - LMSR liquidity parameter (b)
 * @returns Total cost value
 */
export function lmsrCost(quantities: number[], liquidityParam: number): number {
	if (liquidityParam === 0 || quantities.length === 0) {
		return 0
	}

	// Find max for numerical stability (log-sum-exp trick)
	const qOverB = quantities.map(q => q / liquidityParam)
	const maxQ = Math.max(...qOverB)

	// log(sum(exp(x_i))) = max + log(sum(exp(x_i - max)))
	let sumExp = 0
	for (const q of qOverB) {
		sumExp += Math.exp(q - maxQ)
	}

	return liquidityParam * (maxQ + Math.log(sumExp))
}

/**
 * Compute LMSR prices (probabilities) using softmax.
 * p_i = exp(q_i / b) / sum(exp(q_j / b))
 *
 * @param quantities - Current quantities per bucket
 * @param liquidityParam - LMSR liquidity parameter (b)
 * @returns Array of prices (0-1 range, sum to 1)
 */
export function computePrices(quantities: number[], liquidityParam: number): number[] {
	if (liquidityParam === 0 || quantities.length === 0) {
		return quantities.map(() => 1 / Math.max(quantities.length, 1))
	}

	const qOverB = quantities.map(q => q / liquidityParam)
	const maxQ = Math.max(...qOverB)

	const expValues: number[] = []
	let sumExp = 0
	for (const q of qOverB) {
		const expVal = Math.exp(q - maxQ)
		expValues.push(expVal)
		sumExp += expVal
	}

	return expValues.map(exp => exp / sumExp)
}

/**
 * Compute price for a single bucket.
 *
 * @param quantities - Current quantities per bucket
 * @param liquidityParam - LMSR liquidity parameter (b)
 * @param bucket - Bucket index
 * @returns Price for the bucket (0-1 range)
 */
export function computePrice(quantities: number[], liquidityParam: number, bucket: number): number {
	const prices = computePrices(quantities, liquidityParam)
	return prices[bucket] ?? 0.5
}

// =============================================================================
// Buy Quote Calculation
// =============================================================================

export interface LmsrBuyQuote {
	/** Shares that can be purchased */
	shares: number
	/** Cost before fee */
	cost: number
	/** Fee amount */
	fee: number
	/** Total cost (cost + fee) */
	total: number
	/** Average price per share */
	avgPrice: number
	/** Price before trade (0-1) */
	priceBefore: number
	/** Price after trade (0-1) */
	priceAfter: number
	/** Price impact as ratio (0.05 = 5% increase) */
	priceImpact: number
}

/**
 * Calculate buy quote: how many shares for a given USD amount?
 * Uses binary search to find optimal share count.
 *
 * @param quantities - Current quantities per bucket (human-readable)
 * @param liquidityParam - LMSR liquidity parameter
 * @param bucket - Target bucket index
 * @param usdAmount - Amount in USD to spend
 * @param feeBps - Fee in basis points (100 = 1%)
 */
export function calculateBuyQuote(args: {
	quantities: number[]
	liquidityParam: number
	bucket: number
	usdAmount: number
	feeBps: number
}): LmsrBuyQuote {
	const { quantities, liquidityParam, bucket, usdAmount, feeBps } = args

	// Validate inputs
	if (usdAmount <= 0 || bucket < 0 || bucket >= quantities.length) {
		return {
			shares: 0,
			cost: 0,
			fee: 0,
			total: 0,
			avgPrice: 0,
			priceBefore: computePrice(quantities, liquidityParam, bucket),
			priceAfter: computePrice(quantities, liquidityParam, bucket),
			priceImpact: 0,
		}
	}

	// Calculate fee and net amount available for shares
	const feeRate = feeBps / 10000
	const fee = usdAmount * feeRate
	const netAmount = usdAmount - fee

	// Price before trade
	const priceBefore = computePrice(quantities, liquidityParam, bucket)

	// Binary search for shares that cost exactly netAmount
	const shares = findSharesForCost(quantities, liquidityParam, bucket, netAmount)

	// Compute actual cost for these shares
	const quantitiesAfter = [...quantities]
	quantitiesAfter[bucket] = (quantitiesAfter[bucket] ?? 0) + shares
	const actualCost =
		lmsrCost(quantitiesAfter, liquidityParam) - lmsrCost(quantities, liquidityParam)

	// Price after trade (exact, from LMSR)
	const priceAfter = computePrice(quantitiesAfter, liquidityParam, bucket)

	// Calculate metrics
	const avgPrice = shares > 0 ? actualCost / shares : priceBefore
	const priceImpact = priceBefore > 0 ? (priceAfter - priceBefore) / priceBefore : 0

	return {
		shares,
		cost: actualCost,
		fee,
		total: actualCost + fee,
		avgPrice,
		priceBefore,
		priceAfter,
		priceImpact: Math.max(0, priceImpact), // Buying increases price
	}
}

// =============================================================================
// Sell Quote Calculation
// =============================================================================

export interface LmsrSellQuote {
	/** Shares being sold */
	shares: number
	/** Proceeds before fee */
	proceeds: number
	/** Fee amount */
	fee: number
	/** Net proceeds (proceeds - fee) */
	usdcOut: number
	/** Average price per share */
	avgPrice: number
	/** Price before trade (0-1) */
	priceBefore: number
	/** Price after trade (0-1) */
	priceAfter: number
	/** Price impact as ratio (negative for sells) */
	priceImpact: number
}

/**
 * Calculate sell quote: how much USD for selling shares?
 *
 * @param quantities - Current quantities per bucket (human-readable)
 * @param liquidityParam - LMSR liquidity parameter
 * @param bucket - Target bucket index
 * @param shares - Shares to sell
 * @param feeBps - Fee in basis points (100 = 1%)
 */
export function calculateSellQuote(args: {
	quantities: number[]
	liquidityParam: number
	bucket: number
	shares: number
	feeBps: number
}): LmsrSellQuote {
	const { quantities, liquidityParam, bucket, shares, feeBps } = args

	// Validate inputs
	if (shares <= 0 || bucket < 0 || bucket >= quantities.length) {
		return {
			shares: 0,
			proceeds: 0,
			fee: 0,
			usdcOut: 0,
			avgPrice: 0,
			priceBefore: computePrice(quantities, liquidityParam, bucket),
			priceAfter: computePrice(quantities, liquidityParam, bucket),
			priceImpact: 0,
		}
	}

	// Price before trade
	const priceBefore = computePrice(quantities, liquidityParam, bucket)

	// Calculate proceeds: cost(current) - cost(after selling)
	const costBefore = lmsrCost(quantities, liquidityParam)
	const quantitiesAfter = [...quantities]
	quantitiesAfter[bucket] = (quantitiesAfter[bucket] ?? 0) - shares
	const costAfter = lmsrCost(quantitiesAfter, liquidityParam)

	const proceeds = costBefore - costAfter

	// Calculate fee
	const feeRate = feeBps / 10000
	const fee = proceeds * feeRate
	const usdcOut = proceeds - fee

	// Price after trade (exact, from LMSR)
	const priceAfter = computePrice(quantitiesAfter, liquidityParam, bucket)

	// Calculate metrics
	const avgPrice = shares > 0 ? proceeds / shares : priceBefore
	const priceImpact = priceBefore > 0 ? (priceAfter - priceBefore) / priceBefore : 0

	return {
		shares,
		proceeds,
		fee,
		usdcOut,
		avgPrice,
		priceBefore,
		priceAfter,
		priceImpact, // Selling decreases price (negative impact)
	}
}

// =============================================================================
// Internal Helpers
// =============================================================================

/**
 * Binary search to find shares that cost exactly the target amount.
 * Converges in ~20 iterations to high precision.
 */
function findSharesForCost(
	quantities: number[],
	liquidityParam: number,
	bucket: number,
	targetCost: number,
): number {
	if (targetCost <= 0) {
		return 0
	}

	const initialCost = lmsrCost(quantities, liquidityParam)

	// Estimate upper bound based on current price
	const prices = computePrices(quantities, liquidityParam)
	const currentPrice = prices[bucket] ?? 0.5

	// Upper bound: 2x what we'd get at constant price (accounts for price impact)
	let high = (targetCost / Math.max(currentPrice, 0.01)) * 2
	let low = 0

	const epsilon = 1e-6 // Precision threshold
	const maxIterations = 50

	for (let i = 0; i < maxIterations; i++) {
		const mid = (low + high) / 2
		const quantitiesNew = [...quantities]
		quantitiesNew[bucket] = (quantitiesNew[bucket] ?? 0) + mid

		const newCost = lmsrCost(quantitiesNew, liquidityParam)
		const cost = newCost - initialCost

		if (Math.abs(cost - targetCost) < epsilon) {
			return mid
		}

		if (cost < targetCost) {
			low = mid
		} else {
			high = mid
		}
	}

	// Return best estimate (use low to ensure we don't exceed budget)
	return low
}
